import { getDefaultStore } from "jotai"
import { dailyStatIncrementsForSales, getSalesOnDateRange, getWriteOffsOnDateRange, getProductsMovementOnDateRange, getStatsForDays, fieldValue, getComplexSalesOnDateRange, dailyStatIncrementsForComplexSales, getSuppliesOnDateRange, getProductStatsForDays, getSpendingsOnDateRange, dailyStatIncrementsForSpendings, getCashboxTransactionsOnDateRange, dailyStatIncrementsForCashboxTransactions, dailyStatIncrementsForReturns } from "../global/Firebase/firebase"
import { getWorkingHoursOnDateRange, saveWorkingHours, monthlyStatIncrementsForWorkingHours, dailyStatIncrementsForWorkingHours } from "../global/Firebase/workingHours"
import { dateRangeWithTimezoneAndAutoclose, getDailyStatID, getLocalStoreDate, getMonthlyStatDocID, normalizeDate } from "./timezoneFunctions"
import { constantsAtom, dailyStatsAtom, dbAtom, inactiveUsersAtom, organizationIdAtom, productsDictAtom, rolesDictAtom, storesDictAtom, userInDifferentTimeZoneAtom, usersDictAtom } from "../global/AtomsProvider"
import { addDays, format } from "date-fns"
import * as Sentry from "@sentry/react-native"
import { dbWriteLogger } from "./databaseWriteLogger"
import { filterUndefined, shouldUseSupabase, snapshotExists } from "../global/Firebase/types"
import { reportFirestoreOnlyWriterUsage } from "../global/analytics/firestoreOnlyWriterUsage"


/**
 * Generates a product report based on product movements within a date range
 * 
 * This function analyzes product movements (sales, write-offs, supplies) and creates
 * a comprehensive report showing quantities and totals for each product. It can generate
 * either an 'original' report (grouping all movements) or a 'compare' report (separating
 * by movement type for comparison purposes).
 * 
 * @param {Object} range - Date range object with startDate and endDate
 * @param {string} store - Store identifier to filter movements
 * @param {string} type - Report type: 'original' (default) or 'compare'
 * @param {boolean} save - Whether to save the report to the database
 * @returns {Object} Product report object with movement data grouped by product ID
 */
export const generateProductReportForDateFromMovements = async (range, store, type='original', save=false) => {
  let jotaiStore =  getDefaultStore()
  let db = jotaiStore.get(dbAtom)
  let movements = await getProductsMovementOnDateRange(range, true, store)
  if (type === 'original') {
    // group movements by product
    let movementsByProduct = {}
    for (let movement of movements) {
      movementsByProduct[movement.productID] = {...(movementsByProduct[movement.productID] || {}), 
        [movement.type]: (movement.increment || 0) + (movementsByProduct[movement.productID]?.[movement.type] || 0),
        totalMovement: (movement.increment || 0) + (movementsByProduct[movement.productID]?.totalMovement || 0),
      }
    }
    // find the earliest movement for each product
    for (let productID in movementsByProduct) {
      let productMovements = movements.filter(movement => movement.productID === productID);
      if (productMovements.length > 0) {
        movementsByProduct[productID] = {...(movementsByProduct[productID] || {}), 
          earliestMovement: productMovements[productMovements.length - 1],
          latestMovement: productMovements[0],
        }
      }
    }
    if (save) {
      const statDocName = format(range.startDate, 'd-M-y') + '-productMovementsReport-' + store
      reportFirestoreOnlyWriterUsage('generateProductReportForDateFromMovements', db?.id)
      await db.collection('dailyProductStats').doc(statDocName).set({...movementsByProduct, range, store})
    }
    console.log('product report generated')
    return {...movementsByProduct, range, store}
  } else if (type === 'compare') {
    let movementsByProduct = {}
    for (let movement of movements) {
      if (movement.type === 'sale' || movement.type === 'deleteSale') {
        movementsByProduct[movement.productID] = {...(movementsByProduct[movement.productID] || {}), 
          saleQuantity: (movement.increment || 0) + (movementsByProduct[movement.productID]?.saleQuantity || 0),
          writeOffQuantity: (movementsByProduct[movement.productID]?.writeOffQuantity || 0),
          supplyQuantity: (movementsByProduct[movement.productID]?.supplyQuantity || 0),
        }
      } else if (movement.type === 'writeOff' || movement.type === 'deleteWriteOff') {
        movementsByProduct[movement.productID] = {...(movementsByProduct[movement.productID] || {}), 
          writeOffQuantity: (movement.increment || 0) + (movementsByProduct[movement.productID]?.writeOffQuantity || 0),
          saleQuantity: (movementsByProduct[movement.productID]?.saleQuantity || 0),
          supplyQuantity: (movementsByProduct[movement.productID]?.supplyQuantity || 0),
        }
      } else if (movement.type === 'supply' || movement.type === 'deleteSupply') {
        movementsByProduct[movement.productID] = {...(movementsByProduct[movement.productID] || {}), 
          supplyQuantity: (movement.increment || 0) + (movementsByProduct[movement.productID]?.supplyQuantity || 0),
          saleQuantity: (movementsByProduct[movement.productID]?.saleQuantity || 0),
          writeOffQuantity: (movementsByProduct[movement.productID]?.writeOffQuantity || 0),
        }
      }
    }
    return movementsByProduct
  }
}

/**
 * Generates a product report for a specific date from source documents (sales, supplies, write-offs)
 * 
 * This function creates a comprehensive product report by analyzing actual source documents
 * (sales, supplies, write-offs) rather than movement records. It calculates quantities,
 * amounts, margins, and profits for each product on the specified date.
 * 
 * @param {Date} date - The date for which to generate the report
 * @param {string} store - Store identifier to filter data
 * @returns {Object} Product report object with detailed financial and quantity data
 */
export const generateProductReportForDate = async (date, store) => {
  let jotaiStore =  getDefaultStore()
  let db = jotaiStore.get(dbAtom)
  let productsDict = jotaiStore.get(productsDictAtom)
  let localRange = dateRangeWithTimezoneAndAutoclose({startDate: date, endDate: date}, [store])
  var sales = await getSalesOnDateRange(db, localRange, true, store)
  var writeOffs = await getWriteOffsOnDateRange(db, localRange, true, store)
  var supplies = await getSuppliesOnDateRange(db, localRange, true, store, null, false)
  var products = {}

  for (const [saleIndex, sale] of sales.entries()) {
    for (const [index, cartItem] of sale.cartItems.entries()) {
      products[(cartItem.id === 'customProduct' ? ('customProduct' + cartItem.name) : cartItem.id)] = {...products[(cartItem.id === 'customProduct' ? ('customProduct' + cartItem.name) : cartItem.id)],
        name: cartItem.name,
        categoryID: cartItem.categoryID ?? null,
        supplierID: cartItem.supplierID ?? null,
        saleQuantity: (cartItem.quantity || 0) + (products[cartItem.id]?.saleQuantity || 0),
        saleAmount: (cartItem.finalItemPrice || 0) + (products[cartItem.id]?.saleAmount || 0),
        checkQuantity: 1 + (products[cartItem.id]?.checkQuantity || 0),
        purchaseAmount: ((cartItem.quantity * cartItem.purchasePrice) || 0) + (products[cartItem.id]?.purchaseAmount || 0),
        packageAmount: ((cartItem.quantity * cartItem.packagePrice) || 0) + (products[cartItem.id]?.packageAmount || 0),
        writeOffQuantity: 0,
        supplyQuantity: 0,
      }
    }
  }

  for (const [index, writeOff] of writeOffs.entries()) {
    products[writeOff.product.id] = {...products[writeOff.product.id],
      name: products[writeOff.product.id]?.name || writeOff.product.name || 'deleted',
      categoryID: products[writeOff.product.id]?.categoryID || writeOff.product.categoryID || 'deleted',
      supplierID: products[writeOff.product.id]?.supplierID || writeOff.product.supplierID || 'deleted',
      writeOffAmount: (products[writeOff.product.id]?.writeOffAmount || 0) + (writeOff.totalSum || 0),
      writeOffQuantity: (products[writeOff.product.id]?.writeOffQuantity || 0) + (writeOff.quantity || 0),
      saleQuantity: (products[writeOff.product.id]?.saleQuantity || 0),
      supplyQuantity: (products[writeOff.product.id]?.supplyQuantity || 0),
    }
  }

  for (const [index, supply] of supplies.entries()) {
    for (let item of supply.items) {
      products[item.id] = {...products[item.id],
        name: products[item.id]?.name || item.name || 'deleted',
        categoryID: products[item.id]?.categoryID || productsDict[item.id]?.categoryID || 'deleted',
        supplierID: products[item.id]?.supplierID || productsDict[item.id]?.supplierID || 'deleted',
        supplyAmount: (products[item.id]?.supplyAmount || 0) + ((item.purchasePrice || 0) * (item.quantity || 0)),
        supplyQuantity: (products[item.id]?.supplyQuantity || 0) + (item.quantity || 0),
        saleQuantity: (products[item.id]?.saleQuantity || 0),
        writeOffQuantity: (products[item.id]?.writeOffQuantity || 0),
      }
    }
  }

  for (let key in products) {
    var newItem = {...products[key]}
    newItem['margin'] = (newItem.saleAmount || 0) - (newItem.purchaseAmount || 0) - (newItem.packageAmount || 0)
    newItem['profit'] = (newItem.margin || 0) - (newItem.writeOffAmount || 0)
    products[key] = newItem
  }

  const statDocName = format(date, 'd-M-y') + '-productReport-' + store
  // ATL-577: strip undefined nested fields (e.g. categoryID/supplierID for an
  // uncategorized product) — Firestore rejects undefined and fails the whole .set()
  // (Sentry 27Q). Guard the write so a rejected report doc never crashes the caller.
  const payload = filterUndefined({...products, date: date, store})
  reportFirestoreOnlyWriterUsage('generateProductReportForDate', db?.id)
  try {
    await db.collection('dailyProductStats').doc(statDocName).set(payload)
  } catch (error) {
    Sentry.captureException(error, { tags: { feature: 'productReport', books_write_error: 'daily-product-report-write' }, extra: { statDocName, store } })
  }
  // validateProductStocks(date, products, store)
  return {...products, date: date, store}
}

/**
 * Validates product stock levels by comparing movement calculations with stock snapshots
 * 
 * This function retrieves starting and ending stock snapshots for a given date,
 * calculates expected stock levels based on product movements, and compares them
 * to identify any discrepancies in stock tracking.
 * 
 * @param {Date} date - The date to validate stock levels for
 * @param {string} store - Store identifier to filter data
 * @returns {Promise<void>} Logs validation results to console
 */
export const validateProductStocksFromMovements = async (date, store) => {
  let localDate = getLocalStoreDate(store, date)
  let jotaiStore =  getDefaultStore()
  let db = jotaiStore.get(dbAtom)
  let productsDict = jotaiStore.get(productsDictAtom)
  // get product stock snapshots 
  let startingStatId = getDailyStatID(store, localDate)
  let startingSnap = await db.collection('productStockSnapshots').doc(startingStatId + '-stock').get()
  let startingStockSnapshot = startingSnap.data()
  if (snapshotExists(startingSnap)) {
    // console.log('starting stock snapshot', normalizeDate(startingStockSnapshot.date), startingStockSnapshot)
  } else {
    console.log('starting stock snapshot does not exist')
  }
  let endingStatId = getDailyStatID(store, addDays(localDate, 1))
  let endingSnap = await db.collection('productStockSnapshots').doc(endingStatId + '-stock').get()
  let endingStockSnapshot = endingSnap.data()
  if (!snapshotExists(endingSnap)) {
    console.log('ending stock snapshot does not exist')
    endingStockSnapshot = {}
    for (let key in startingStockSnapshot) {
      endingStockSnapshot[key] = productsDict[key]?.storeStock[store]
    }
    endingStockSnapshot.date = new Date()
  }
  // get product report from movement from db
  // let productReport = await db.collection('dailyProductStats').doc(format(localDate, 'd-M-y') + '-productMovementsReport-' + store).get()
  // let movementsByProduct = productReport.data()
  // check if product report exists, if not generate it
  // if (!productReport.exists) {
  let movementsByProduct = await generateProductReportForDateFromMovements({startDate: startingStockSnapshot.date, endDate: endingStockSnapshot.date}, store, 'original', false)
  // }

  // compare startingStockSnapshot and earliest movement previous in stock
  for (let key in movementsByProduct) {
    let movement = movementsByProduct[key]
    if (movement.earliestMovement) {
      if (Math.abs(movement.earliestMovement.previousInStock - startingStockSnapshot[key]) > 0.01) {
        console.log('stock start mismatch for product ' + productsDict[key]?.name + ' ' + startingStockSnapshot[key] + ' vs ' + movement.earliestMovement.previousInStock)
        console.log('date of startingStockSnapshot vs movement: ', normalizeDate(startingStockSnapshot.date), normalizeDate(movement.earliestMovement.date))
      }
      if (Math.abs(movement.latestMovement.previousInStock + movement.latestMovement.increment - endingStockSnapshot[key]) > 0.1) {
        console.log('stock end mismatch for product ' + productsDict[key]?.name + ' ' + endingStockSnapshot[key] + ' vs ' + (movement.latestMovement.previousInStock + movement.latestMovement.increment))
      }
    }
  }
  let stockShouldBe = {}
  // add total movement to starting stock
  for (let key in movementsByProduct) {
    stockShouldBe[key] = startingStockSnapshot[key] + movementsByProduct[key].totalMovement
  }
  for (let key in stockShouldBe) {
    if (Math.abs(stockShouldBe[key] - endingStockSnapshot[key]) > 0.01) {
      console.log('stock mismatch for product ' + productsDict[key]?.name + ' ' + stockShouldBe[key] + ' vs ' + endingStockSnapshot[key])
    }
  }
}

/**
 * Validates product stock levels for a date range by iterating through each date
 * 
 * This function validates stock levels for each date within the specified range
 * by calling validateProductStocksFromMovements for each individual date.
 * 
 * @param {Object} range - Date range object with startDate and endDate
 * @param {string} store - Store identifier to filter data
 * @returns {Promise<void>} Logs validation results for each date
 */
export const validateProductStocksFromMovementsForRange = async (range, store) => {
  let newRange = range
  let date = new Date(newRange.startDate)
  while (date <= newRange.endDate) {
    console.log('date', date)
    await validateProductStocksFromMovements(date, store)
    date = new Date(date.setDate(date.getDate() + 1))
  }
}

/**
 * Validates product movements by checking increment calculations and date consistency
 * 
 * This function analyzes movement data to ensure that:
 * - Previous stock levels match expected values between consecutive movements
 * - Increment values are valid numbers
 * - Date relationships between linked documents are reasonable
 * - Server save dates are within acceptable timeframes
 * 
 * @param {Array} movements - Array of movement documents grouped by date
 * @returns {Array} Modified movements array with validation flags (isCorrect, pastDate, savedToServerLater)
 */
export const validateMovementsWithIncrements = (movements) => {
  let docs = movements
  let jotaiStore =  getDefaultStore()
  let productsDict = jotaiStore.get(productsDictAtom)
  for (const [index1, dateData] of docs.entries()) {
    for (const [index2, value] of dateData.data.entries()) {
      // compare to next movement previous instock
      if (index2 < dateData.data.length - 1) {
        if ((dateData.data[index2 + 1].previousInStock + dateData.data[index2 + 1].increment) === value.previousInStock) {
          value.isCorrect = true
        } else {
          value.isCorrect = false
          console.log('incorrect previous in stock', productsDict[value.productID]?.name, normalizeDate(value.date), value)
        }
      } else if (index1 < docs.length - 1) {
        if (docs[index1 + 1].data[0].previousInStock + docs[index1 + 1].data[0].increment === value.previousInStock) {
          value.isCorrect = true
        } else {
          value.isCorrect = false
          console.log('incorrect previous in stock', productsDict[value.productID]?.name, normalizeDate(value.date), value)
        }
      } else {
        value.isCorrect = null // first doc in array
      }
      if (isNaN(value.increment)) {
        console.log('NaN increment', productsDict[value.productID]?.name, normalizeDate(value.date), value)
      }
      // compare linkedDocDate and date, if more than 500 minutes, show notice
      if (value.linkedDocDate && value.date) {
        let diff = Math.abs(normalizeDate(value.linkedDocDate) - normalizeDate(value.date))
        if (diff > 30000000) {
          value.pastDate = true
          if (value.isCorrect == false) {
            console.log('past date', productsDict[value.productID]?.name, normalizeDate(value.date), value)
          }
        }
      }
      // compare date and serverSaveDate, if more than 5 minutes show notice
      if (value.date && value.clientSaveDate) {
        let diff = Math.abs(normalizeDate(value.date) - normalizeDate(value.clientSaveDate))
        if (diff > 300000) {
          value.savedToServerLater = true
          if (value.isCorrect == false) {
            console.log('saved to server later', productsDict[value.productID]?.name, normalizeDate(value.date), value)
          }
        }
      }
    }
  }
  return docs
}

/**
 * Retrieves product movements for a date range and validates their consistency
 * 
 * This function fetches product movements, groups them by product and date,
 * then validates the movement data using validateMovementsWithIncrements.
 * If no range is provided, it uses the last validation date or defaults to 6 days ago.
 * 
 * @param {Object} range - Optional date range object with startDate and endDate
 * @param {string} store - Store identifier to filter movements
 * @returns {Promise<void>} Logs validation results to console
 */
export const getMovementsAndValidate = async (range, store) => {
  if (!range) {
    let jotaiStore =  getDefaultStore()
    let db = jotaiStore.get(dbAtom)
    let constants = jotaiStore.get(constantsAtom)
    let lastValidationDate = constants.validationLog?.lastMovementIncrementValidationDate
    if (lastValidationDate) {
      range = {startDate: normalizeDate(lastValidationDate), endDate: new Date()}
    } else {
      range = {startDate: addDays(new Date(), -6), endDate: new Date()}
    }
  }
  let movements = await getProductsMovementOnDateRange(range, true, store)
  // group movements first by product, then by date
  let movementsByProduct = {}
  for (let movement of movements) {
    let date = format(getLocalStoreDate(store, normalizeDate(movement.date)), 'd/M/y EEEE')
    if (!movementsByProduct[movement.productID]) {
      movementsByProduct[movement.productID] = {}
    }
    if (!movementsByProduct[movement.productID][date]) {
      movementsByProduct[movement.productID][date] = {
        title: date,
        data: []
      }
    }
    movementsByProduct[movement.productID][date]['data'].push(movement)
  }
  // make an array of movementsByProduct
  let movementsByProductArray = {}
  for (let product of Object.keys(movementsByProduct)) {
    movementsByProductArray[product] = []
    for (let date of Object.keys(movementsByProduct[product])) {
      movementsByProductArray[product].push(movementsByProduct[product][date])
    }
  }
  for (let product of Object.keys(movementsByProductArray)) {
    validateMovementsWithIncrements(movementsByProductArray[product])
  }
}

/**
 * Compares product movements with source documents to ensure data consistency
 * 
 * This function validates that movement records match the actual source documents
 * (sales, write-offs, supplies) by comparing quantities for each product type.
 * It helps identify discrepancies between movement tracking and actual transactions.
 * 
 * @param {Object} range - Date range object with startDate and endDate
 * @param {string} store - Store identifier to filter data
 * @param {boolean} update - Whether to update data if discrepancies are found
 * @returns {Promise<void>} Logs comparison results to console
 */
export const compareProductMovementsAndSourceDocs = async (range, store, update=false) => {
  // compare movements product report to product report from source documents - actual sales, write offs, supplies... - no duplicates and no missing

  let jotaiStore = getDefaultStore()
  let productsDict = jotaiStore.get(productsDictAtom)
  let localRange = dateRangeWithTimezoneAndAutoclose(range, [store])
  let date = addDays(localRange.startDate, -1)
  do {
    date = addDays(date, 1)
    let localRangeSingleDate = dateRangeWithTimezoneAndAutoclose({startDate: date, endDate: date}, [store])
    console.log('date', date)

    // group movements by product
    let movementsByProduct = await generateProductReportForDateFromMovements(localRangeSingleDate, store, 'compare', false)
    
    let productReport = await generateProductReportForDate(date, store)
    delete productReport.store
    delete productReport.date

    // compare with product report
    let allKeys = [...new Set([...Object.keys(movementsByProduct), ...Object.keys(productReport)])]
    for (let key of allKeys) {
      let movement = movementsByProduct[key]
      let report = productReport[key]
      if (!report) report = {name: productsDict[key]?.name, saleQuantity: 0, writeOffQuantity: 0, supplyQuantity: 0}
      if (!movement) movement = {name: productsDict[key]?.name, saleQuantity: 0, writeOffQuantity: 0, supplyQuantity: 0}
      let str =  productsDict[key]?.name + '\n'
      if ((movement.saleQuantity !== undefined || report.saleQuantity !== undefined) && -movement.saleQuantity !== report.saleQuantity) {
        str += 'sale quantity mismatch ' + movement.saleQuantity + ' vs ' + report.saleQuantity + '\n'
      }
      if ((movement.writeOffQuantity !== undefined || report.writeOffQuantity !== undefined) && -movement.writeOffQuantity !== report.writeOffQuantity) {
        str += 'write off quantity mismatch ' + movement.writeOffQuantity + ' vs ' + report.writeOffQuantity + '\n'
      }
      if ((movement.supplyQuantity !== undefined || report.supplyQuantity !== undefined) && movement.supplyQuantity !== report.supplyQuantity) {
        str += 'supply quantity mismatch ' + movement.supplyQuantity + ' vs ' + report.supplyQuantity + '\n'
      }
      if (str !== (productsDict[key]?.name + '\n')) {
        console.log(str)
      }
    }
  } while (format(date, 'd/M/y E') !== format(localRange.endDate, 'd/M/y E'))


}

/**
 * Validates daily statistics by comparing calculated values with stored daily stats
 * 
 * This function validates various aspects of daily statistics including sales,
 * spendings, and working hours by comparing calculated values from source documents
 * with the stored daily statistics. It can optionally update discrepancies.
 * 
 * @param {Date} date - The date to validate statistics for
 * @param {string} store - Store identifier to filter data
 * @param {boolean} update - Whether to update statistics if discrepancies are found
 * @param {Object} options - Validation options (sales, spendings, workingHours)
 * @param {Object} prefetchedData - Optional pre-fetched data to avoid duplicate queries
 * @param {boolean} log - Whether to log detailed information
 * @param {boolean} automaticFixes - Whether to apply automatic fixes
 * @returns {Promise<Array>} `[isValid, fixes, calculatedStats, meta]`. `meta.applied` says whether
 *   the repair was actually WRITTEN, `meta.suppressedByTimezone` whether it was computed and
 *   withheld, and `meta.skipped` names the reason when the function declined to look at all — all
 *   three are present on EVERY return, so a caller may read any of them without first working out
 *   which exit it came from. It is ALWAYS a 4-tuple, never `undefined` —
 *   `compareProductReportAndDailyStats` destructures the result directly, so a bare `undefined`
 *   return was a latent TypeError on every early exit (ATL-1101).
 */
/**
 * The `options` whose SOURCE follows `shouldUseSupabase()`.
 *
 * `getSalesOnDateRange`, `getComplexSalesOnDateRange`, `getSpendingsOnDateRange` and
 * `getCashboxTransactionsOnDateRange` each branch on the read toggle. `getWorkingHoursOnDateRange`
 * (`global/Firebase/workingHours.ts:437`) does NOT — it reads Firestore unconditionally — which is
 * why `workingHours` is deliberately absent from this list. See the refusal below.
 */
const TOGGLED_SOURCE_OPTIONS = ['sales', 'spendings', 'cashboxTransactions']

export const validateDailyStats = async (date, store, update=false, options, prefetchedData=null, log=false, automaticFixes=false, screenName=null) => {
  /**
   * Uniform shape for every exit that never got as far as comparing anything.
   *
   * The first element is `false`, NOT `true`. Every one of these exits means "this document was not
   * verified", and four of them (`*-query-failed`) mean a read actually errored — reporting that as
   * `isValid: true` would be the instrument announcing a clean result it never measured, which is
   * the failure mode this whole change exists to remove. `meta.skipped` names which exit it was.
   */
  const skip = (reason) => [false, {}, {}, { applied: false, skipped: reason, suppressedByTimezone: false }]

  if (store === 'all') return skip('store-is-all')

  // ATL-1153 — a day with no store names no document and bounds no query.
  //
  // `countEmployeeBonuses` calls this as `validateDailyStats(dateObj, store || '', ...)`, and every
  // getter below treats a falsy store as "do not filter by store": `getWorkingHoursOnDateRange`
  // (workingHours.ts:447) applies its `where('store','==',…)` clause only `if (store)`. So an empty
  // store recomputed ABSOLUTE totals from EVERY store's records and wrote them to `<d-M-y>-` — a
  // document belonging to no store, built from rows belonging to all of them. Both halves are
  // wrong, so the day is not looked at rather than half-looked-at.
  if (!store) return skip('store-missing')

  // ATL-1101 — REFUSE to recompute the Firestore aggregate out of the Supabase read path.
  //
  // Everything below writes absolute values into `dailyStats/<statId>` in FIRESTORE, but most of it
  // derives them from getters that follow `shouldUseSupabase()`. When reads are on Supabase,
  // `getSalesOnDateRange` goes to `GET /sales`, whose PostgREST response is capped at 1,000 rows and
  // truncates SILENTLY — so on a busy day this would compute a total short by however many sales
  // fell past the cap and then overwrite a correct `dailyStats` doc with it. Recomputing one store's
  // books from a different store is the wrong operation even when it is not truncated.
  //
  // Nothing is lost by declining: in Supabase-read mode the Sales report reads the `daily_stats`
  // VIEW (`getStatsForDays` branches on the same toggle), which is derived from the sale rows by
  // construction and cannot drift. The Firestore doc this would "repair" is on no read path.
  //
  // ⚠️ The condition names the HAZARD, not the mode. An earlier revision refused on
  // `shouldUseSupabase()` alone, which also blocked the ONE branch that cannot be row-capped:
  // `countEmployeeBonuses` (`global/Firebase/workingHours.ts:1261`) calls this with
  // `{workingHours: true}`, whose source reads Firestore unconditionally — and it is the ONLY writer
  // of `dailyStats.employeeBonusAmount` in the app. Blocking it would silently stop reconciling
  // salary and bonus figures for every cut-over org, which surfaces the moment anyone uses the
  // documented read-rollback. A guard whose condition names an entity rather than the capability
  // that makes harm reachable blocks the wrong half of the population.
  const toggledSources = TOGGLED_SOURCE_OPTIONS.filter((key) => options?.[key])
  if (shouldUseSupabase() && toggledSources.length > 0) return skip('supabase-read-mode')

  let jotaiStore =  getDefaultStore()
  let constants = jotaiStore.get(constantsAtom)
  let db = jotaiStore.get(dbAtom)
  let organizationId = jotaiStore.get(organizationIdAtom)
  let usersDict = jotaiStore.get(usersDictAtom)
  // ATL-1101 — inactive employees count too. `usersDictAtom` holds ONLY users with `active: true`
  // (AppStack.js:876 files the rest into `inactiveUsersAtom`), so building the per-employee key list
  // from it alone left `<uid>SalesAmount` / `<uid>SalesQuantity` / `<uid>Returns*` for every departed
  // cashier permanently unrepairable — while the Sales report still renders those columns. 7 of the
  // 20 users at the org that reported this are inactive.
  let inactiveUsersDict = jotaiStore.get(inactiveUsersAtom)
  let allUsers = [...Object.values(usersDict || {}), ...Object.values(inactiveUsersDict || {})]
  let userInDifferentTimeZone = jotaiStore.get(userInDifferentTimeZoneAtom)
  
  // Initialize logger if not already initialized
  if (!dbWriteLogger.db || !dbWriteLogger.organizationId) {
    dbWriteLogger.initialize(db, organizationId)
  }
  
  // Start database write logging with call stack info
  const logId = dbWriteLogger.startFunction('validateDailyStats', {
    store,
    date: date?.toISOString?.() || date,
    screen: screenName || null, // Screen name passed from caller, or extracted from call stack
    options: Object.keys(options || {}).filter(k => options[k]),
    update,
    automaticFixes
  })

  // The union, not `usersDict` alone: an org whose every employee is deactivated has fully LOADED
  // atoms, and this guard exists to catch "not loaded yet", not "nobody is active".
  if (!constants?.paymentMethods || allUsers.length === 0) {
    console.log('no constants/users')
    dbWriteLogger.endFunction(logId)
    return skip('constants-or-users-not-loaded')
  }
  let localDate = getLocalStoreDate(store, date)
  let newRange = dateRangeWithTimezoneAndAutoclose({startDate: localDate, endDate: localDate}, [store])
  let fixes = {}
  // ATL-1101 — the document this call reconciles is the one the COMPARISON WINDOW covers.
  //
  // `newRange` above is `dateRangeWithTimezoneAndAutoclose({startDate: localDate, endDate:
  // localDate})`, and that function takes `localDate`'s CALENDAR day D and returns
  // `[D 00:00 + autoclose, D+1 00:00 + autoclose)` (`timezoneUtils.ts:166-205`). So the window is
  // always business day D, whatever time of day `date` names — and the document holding business
  // day D's increments is `format(localDate, 'd-M-y') + '-' + store`, the autoclose-NAIVE spelling.
  //
  // ⚠️ AN EARLIER REVISION OF THIS BRANCH USED `getDailyStatID` HERE AND THAT WAS BACKWARDS.
  // `getDailyStatID` is autoclose-AWARE: for any `date` whose store-local time is before the
  // cutoff it returns D−1. Measured with the real functions for a store closing at 03:00:
  //
  //     date                              window                       naive     aware
  //     2 Sep 00:00 local (a date picker) 2-9 03:00 .. 3-9 02:59       2-9-2026  1-9-2026
  //     2 Sep 02:10 local (an instant)    2-9 03:00 .. 3-9 02:59       2-9-2026  1-9-2026
  //     2 Sep 14:00 local (trading)       2-9 03:00 .. 3-9 02:59       2-9-2026  2-9-2026
  //
  // The naive spelling matches the window in every row; the aware one diverges in exactly the
  // pre-cutoff rows. Since `fixes` holds ABSOLUTE totals recomputed from the window, the aware
  // spelling writes business day D's totals onto document D−1 — and the Sales report's
  // "Пересчитать" (`SalesStats.js:661`) and the admin recompute (`MiscScreen.js:473`) both pass a
  // Date at 00:00 local, so it would have fired on EVERY recalculate of EVERY autoclose store,
  // overwriting the previous day's real books. Do not "fix" this to the writer's naming again
  // without moving `newRange` to `getStoreBusinessDate` in the same change; the pair has to agree,
  // and today it agrees on the calendar day.
  let statDocName = format(localDate, 'd-M-y') + '-' + store
  let existingDailyStat = {}
  let unifiedSaleStatFields = {} // new

  // ATL-1101 — take the cached document only when it IS this document, by id.
  //
  // This used to be a date test (`format(today) === format(localDate)`), which asks a different
  // question than the one that makes the shortcut safe. `dailyStatsAtom[store]` is a snapshot
  // listener on `getDailyStatID(storeId, new Date())` (`global/AppStack.js:598`) — the
  // autoclose-AWARE current business day — so on an autoclose store between midnight and the
  // cutoff the atom holds D−1 while the date test says "today" for D, and the write lands on the
  // wrong document. Comparing ids removes the whole class: the cache may be used exactly when it
  // holds the document being reconciled.
  //
  // The `shouldUseSupabase()` clause is redundant against that id test and kept deliberately. In
  // Supabase read mode AppStack fills the atom from the `daily_stats` VIEW and stamps
  // `id: dateKey` — a plain `YYYY-MM-DD` (`AppStack.js:519`, key built at
  // `utils/supabaseDataHelpers.js:662`) — which could never equal a `d-M-y-<store>` id; the clause
  // states the intent rather than relying on two id formats never colliding, on a money path.
  if (!shouldUseSupabase()) {
    let dailyStats = jotaiStore.get(dailyStatsAtom)
    let cached = dailyStats?.[store]
    if (cached?.id === statDocName) existingDailyStat = {...cached}
  }
  if (!existingDailyStat?.id) {
    // get existing daily stat
    let dSSnap = await db.collection('dailyStats').doc(statDocName).get()
    existingDailyStat = {...dSSnap.data()}
    existingDailyStat.id = dSSnap.id
  }

  if (options.sales) {
    let sales = []
    if (prefetchedData?.sales) {
      sales = prefetchedData.sales
    } else {
      try {
        sales = await getSalesOnDateRange(db, newRange, true, store)
      } catch(error) {
        console.log('error getting sales', error)
        Sentry.captureMessage('error getting sales in validation', {extra: {error}})
        dbWriteLogger.endFunction(logId)
        return skip('sales-query-failed')
      }
    }
    let saleStatFields = dailyStatIncrementsForSales(sales, false, false)
    let complexSales = []
    if (prefetchedData?.complexSales) {
      complexSales = prefetchedData.complexSales
    } else {
      try {
        complexSales = await getComplexSalesOnDateRange(db, newRange, true, store, null, false)
      } catch(error) {
        console.log('error getting complex sales', error)
        dbWriteLogger.endFunction(logId)
        return skip('complex-sales-query-failed')
      }
    }
    let complexSaleStatFields = dailyStatIncrementsForComplexSales(complexSales, false, false)
    // compare values of saleStatFields and existingDailyStat
    let keysA = [
      'saleAmount',
      'saleQuantity',
      'salesPurchaseAmount',
      'bonusClients',
      'bonusAccumulation',
      'bonusReduction',
      'punchCardReduction',
      'bankPercent',
    ]
    for (let paymentMethod of constants.paymentMethods.data) {
      keysA.push(paymentMethod.id + 'SalesAmount')
      keysA.push(paymentMethod.id + 'SalesQuantity')
      // "...Paid" fields track money actually received (complex-sale payments,
      // maintained by dailyStatIncrementsForComplexSales / add|deleteComplexSalePayment).
      // They drift negative when payments are deleted more times than added, so they
      // must be recomputed here too — otherwise "recalculate daily stats" leaves them stuck.
      keysA.push(paymentMethod.id + 'SalesAmountPaid')
      keysA.push(paymentMethod.id + 'SalesQuantityPaid')
    }
    for (let purchaseMethod of constants.purchaseMethods.data) {
      keysA.push(purchaseMethod.id + 'SalesAmount')
      keysA.push(purchaseMethod.id + 'SalesQuantity')
    }
    for (let wallet of constants.wallets.data) {
      keysA.push(wallet.id + 'SalesAmount')
      keysA.push(wallet.id + 'SalesQuantity')
      // Wallet-scoped "...Paid" counterparts (e.g. "<storeId>-cashSalesAmountPaid"),
      // maintained alongside the payment-method ones and prone to the same drift.
      keysA.push(wallet.id + 'SalesAmountPaid')
      keysA.push(wallet.id + 'SalesQuantityPaid')
    }
    for (let cashier of allUsers) {
      keysA.push(cashier.id + 'SalesAmount')
      keysA.push(cashier.id + 'SalesQuantity')
    }

    for (let key of keysA) {
      unifiedSaleStatFields[key] = (saleStatFields[key] || 0) + (complexSaleStatFields[key] || 0)
    }

    for (let key of keysA) {
      if ((unifiedSaleStatFields[key] && existingDailyStat[key] && unifiedSaleStatFields[key]?.toFixed?.(2) !== existingDailyStat[key]?.toFixed?.(2))
        || (existingDailyStat[key] && !unifiedSaleStatFields[key] && existingDailyStat[key] !== 0) 
        || (!existingDailyStat[key] && unifiedSaleStatFields[key] && unifiedSaleStatFields[key] !== 0)) 
      {
        fixes[key] = unifiedSaleStatFields[key] || 0
      }
    }

    // Validate returns (using same sales data)
    let returnStatFields = dailyStatIncrementsForReturns(sales, false, false)

    let returnKeys = ['returnsAmount', 'returnsQuantity']

    // Add payment method and wallet keys for returns
    for (let paymentMethod of constants.paymentMethods.data) {
      returnKeys.push(paymentMethod.id + 'ReturnsAmount')
      returnKeys.push(paymentMethod.id + 'ReturnsQuantity')
    }
    for (let wallet of constants.wallets.data) {
      returnKeys.push(wallet.id + 'ReturnsAmount')
      returnKeys.push(wallet.id + 'ReturnsQuantity')
    }
    for (let cashier of allUsers) {
      returnKeys.push(cashier.id + 'ReturnsAmount')
      returnKeys.push(cashier.id + 'ReturnsQuantity')
    }

    for (let key of returnKeys) {
      if ((returnStatFields[key] && existingDailyStat[key] && returnStatFields[key]?.toFixed?.(2) !== existingDailyStat[key]?.toFixed?.(2))
        || (existingDailyStat[key] && !returnStatFields[key] && existingDailyStat[key] !== 0) 
        || (!existingDailyStat[key] && returnStatFields[key] && returnStatFields[key] !== 0)) 
      {
        fixes[key] = returnStatFields[key] || 0
      }
    }
  }
  
  if (options.spendings) {
    let spendings = []
    if (prefetchedData?.spendings) {
      spendings = prefetchedData.spendings
    } else {
      try {
        spendings = await getSpendingsOnDateRange(db, newRange, true, store)
      } catch(error) {
        console.log('error getting spendings', error)
        Sentry.captureMessage('error getting sales in validation', {extra: {error}})
        dbWriteLogger.endFunction(logId)
        return skip('spendings-query-failed')
      }
    }
    let spendingStatFields = dailyStatIncrementsForSpendings(spendings, false, false)
    // compare values of saleStatFields and existingDailyStat
    let keysA = [
      'spendingsAmount',
      'spendingsQuantity',
    ]
    for (let wallet of constants.wallets.data) {
      keysA.push(wallet.id + 'SpendingsAmount')
      keysA.push(wallet.id + 'SpendingsQuantity')
    }

    for (let key of keysA) {
      if ((spendingStatFields[key] && existingDailyStat[key] && spendingStatFields[key]?.toFixed?.(2) !== existingDailyStat[key]?.toFixed?.(2))
        || (existingDailyStat[key] && !spendingStatFields[key] && existingDailyStat[key] !== 0) 
        || (!existingDailyStat[key] && spendingStatFields[key] && spendingStatFields[key] !== 0)) 
      {
        fixes[key] = spendingStatFields[key] || 0
      }
    }
  }
 
  if (options.workingHours) {
    // ATL-1153 — `salaryAmount`, `workingHoursQuantity`, `workingDaysQuantity` and
    // `employeeBonusAmount` on `dailyStats/<d-M-y>-<store>` are STORE-day totals over EVERY
    // employee, and everything below writes them as ABSOLUTE values. The set that backs them is
    // therefore exactly:
    //
    //     { wh : wh.store === store AND businessDay(wh) === this document's day }
    //
    // A SUBSET understates it; a SUPERSET overstates it. Neither is detectable by looking at an
    // array somebody handed in — completeness is not a property of the rows, it is a property of
    // the QUERY that produced them. So this branch establishes the set by construction and no
    // longer reads `prefetchedData.workingHours`.
    //
    // What it used to do: take the prefetched set on trust. The only caller that supplies one is
    // `countEmployeeBonuses` (workingHours.ts), and from `SalaryScreen` the set it is working from
    // is ONE employee's records — `workingHoursApi.getWorkingHours({employeeId: user.uid})` on the
    // Supabase path, `.where('employeeID','==',user.uid)` on the Firestore one. Opening one
    // employee's salary screen therefore rewrote that store-day's salary and bonus totals to that
    // employee's figures alone, dropping every colleague's shift, on both read paths and with
    // `automaticFixes: false` so the timezone suppression never stopped it.
    //
    // "Both read paths" is about where the WRITE happens, not about who reads the result: an org
    // reading Supabase gets these four figures from `get_daily_stats`, which sums them from
    // `working_hours` itself (ATL-1661), so for it this document is repaired but unread — it is
    // what a read-rollback returns to. For every org still reading Firestore it is the figure on
    // the screen.
    //
    // The cost of establishing it here is one Firestore read per day whose bonuses actually
    // changed. `getWorkingHoursOnDateRange` reads Firestore unconditionally (it is the reason
    // `workingHours` is absent from TOGGLED_SOURCE_OPTIONS), so this is not exposed to the
    // PostgREST row cap that makes the sales recompute refuse in Supabase-read mode.
    let workingHours = []
    try {
      // `null` userId — every employee. `store` — this store only, and the refusal at the top of
      // this function is what guarantees it is non-empty, because a falsy store makes this query
      // drop its store clause entirely.
      workingHours = await getWorkingHoursOnDateRange(newRange, null, true, store)
    } catch(error) {
      // This read used to be skipped whenever a caller prefetched, so its failure was unreachable
      // from `countEmployeeBonuses`. It is reachable now, and a failed read must not be allowed to
      // compute a total from zero rows and write it over a correct document.
      console.log('error getting working hours', error)
      Sentry.captureMessage('error getting working hours in validation', {extra: {error}})
      dbWriteLogger.endFunction(logId)
      return skip('working-hours-query-failed')
    }

    // Defence in depth behind the store clause above. If the query ever returns a cross-store row,
    // it must not be summed into a document it does not belong to — and it must not be dropped
    // silently either, because that would mean the clause did not hold.
    const foreignStoreRecords = workingHours.filter((workingHour) => workingHour?.store !== store)
    if (foreignStoreRecords.length > 0) {
      workingHours = workingHours.filter((workingHour) => workingHour?.store === store)
      Sentry.captureMessage(
        'working-hours query returned records belonging to another store',
        {
          level: 'warning',
          fingerprint: ['working-hours-cross-store', organizationId || 'unknown', store],
          contexts: {
            working_hours_scope: {
              store,
              statId: statDocName,
              droppedCount: foreignStoreRecords.length,
              droppedStores: Array.from(new Set(foreignStoreRecords.map((wh) => wh?.store ?? null))),
            },
          },
        }
      )
    }

    // A caller that still supplies working hours is not degraded silently: the argument is ignored
    // on purpose, and saying so is the difference between a deliberate contract and a value that
    // quietly stopped being read. `PrefetchedData` no longer declares the key, so a TypeScript
    // caller gets this as a compile error instead.
    if (prefetchedData?.workingHours) {
      Sentry.captureMessage(
        'prefetched workingHours ignored — store-day totals are recomputed from the store\'s own day (ATL-1153)',
        {
          level: 'warning',
          fingerprint: ['prefetched-working-hours-ignored', organizationId || 'unknown'],
          contexts: {
            working_hours_scope: {
              store,
              statId: statDocName,
              screenName,
              prefetchedCount: prefetchedData.workingHours.length,
              authoritativeCount: workingHours.length,
            },
          },
        }
      )
    }

    let workingHourStatFields = {}
    for (let workingHour of workingHours) {
      let workingHourStatField = dailyStatIncrementsForWorkingHours(workingHour, false, false)
      for (let key of Object.keys(workingHourStatField)) {
        workingHourStatFields[key] = (workingHourStatFields[key] || 0) + (workingHourStatField[key] || 0)
      }
    }
    // `|| 0` — `employeeBonusAmount` is OPTIONAL (`WorkingHoursInfo`, workingHours.ts:58) and a
    // never-bonused shift simply omits it. Without the guard one such record makes this sum `NaN`;
    // `NaN` is falsy, so the "recomputed is empty but the document is not" clause below fires and
    // `NaN || 0` WRITES 0 over a correct store-day bonus total. Reading the whole store day rather
    // than the caller's already-bonus-processed subset is what brings such records into range, so
    // the scope fix above is what made this reachable. Every sibling reduce in `workingHours.ts`
    // (:656, :892, :911) already guards it the same way; this is an absent optional field meaning
    // zero, not a fallback papering over a bad value.
    workingHourStatFields.employeeBonusAmount = workingHours.reduce((acc, workingHour) => acc + (workingHour.employeeBonusAmount || 0), 0)
    // ATL-1153 — a FIXED key set, not `Object.keys(workingHourStatFields)`.
    //
    // `dailyStatIncrementsForWorkingHours` emits `salaryAmount` plus one of `workingHoursQuantity`
    // / `workingDaysQuantity` per record, and `employeeBonusAmount` is summed above — so these four
    // are every key this branch owns. Reading the key set off the RECOMPUTED object made the repair
    // depend on what the day happened to contain: on a day with zero working-hour records only
    // `employeeBonusAmount` exists, so the bonus was zeroed while a stale `salaryAmount` and the
    // stale quantities survived. A half-repaired money document is worse than an unrepaired one.
    //
    // Zeroing is safe on a document that never carried these keys: the comparison below writes a 0
    // only when the EXISTING value is truthy, so an absent key stays absent rather than being
    // minted at 0. (An empty result here means the query genuinely found no shifts — a failed read
    // returns `working-hours-query-failed` above and never reaches this point.)
    let keys = ['salaryAmount', 'workingHoursQuantity', 'workingDaysQuantity', 'employeeBonusAmount']
    // compare values of workingHourStatFields and existingDailyStat
    for (let key of keys) {
      if ((workingHourStatFields[key] && existingDailyStat[key] && workingHourStatFields[key]?.toFixed?.(2) !== existingDailyStat[key]?.toFixed?.(2))
        || (existingDailyStat[key] && !workingHourStatFields[key] && existingDailyStat[key] !== 0) 
        || (!existingDailyStat[key] && workingHourStatFields[key] && workingHourStatFields[key] !== 0)) 
      {
        fixes[key] = workingHourStatFields[key] || 0
      }
    }
  }

  if (options.cashboxTransactions) {
    let cashboxTransactions = []
    if (prefetchedData?.cashboxTransactions) {
      cashboxTransactions = prefetchedData.cashboxTransactions
    } else {
      try {
        cashboxTransactions = await getCashboxTransactionsOnDateRange(db, newRange, true, store)
      } catch(error) {
        console.log('error getting cashbox transactions', error)
        Sentry.captureMessage('error getting cashbox transactions in validation', {extra: {error}})
        dbWriteLogger.endFunction(logId)
        return skip('cashbox-query-failed')
      }
    }
    let cashboxTransactionStatFields = dailyStatIncrementsForCashboxTransactions(cashboxTransactions, false, false)
    
    // Get cash wallet for the store
    let cashWallet = null
    for (let wallet of constants.wallets.data) {
      if (wallet.storeId === store && wallet.id.includes('cash')) {
        cashWallet = wallet.id
        break
      }
    }
    
    // compare values of cashboxTransactionStatFields and existingDailyStat
    let keysA = [
      'shiftOpened',
      'shiftOpenedTime',
      'shiftClosed',
      'shiftClosedTime',
    ]
    
    // Add cash wallet fields if wallet exists, otherwise use legacy store-based fields
    if (cashWallet) {
      keysA.push(cashWallet + 'DepositsAmount')
      keysA.push(cashWallet + 'WithdrawalsAmount')
      keysA.push(cashWallet + 'StartAmount')
    } else {
      // Fallback to legacy field names for backward compatibility
      keysA.push(store + '-cashDepositsAmount')
      keysA.push(store + '-cashWithdrawalsAmount')
      keysA.push(store + '-cashStartAmount')
    }

    // Add dynamic keys for end amounts and system end amounts
    for (let paymentMethod of constants.paymentMethods.data) {
      keysA.push(paymentMethod.id + 'EndAmount')
      keysA.push(paymentMethod.id + 'SystemEndAmount')
    }

    for (let key of keysA) {
      if ((cashboxTransactionStatFields[key] && existingDailyStat[key] && cashboxTransactionStatFields[key]?.toFixed?.(2) !== existingDailyStat[key]?.toFixed?.(2))
        || (existingDailyStat[key] && !cashboxTransactionStatFields[key] && existingDailyStat[key] !== 0) 
        || (!existingDailyStat[key] && cashboxTransactionStatFields[key] && cashboxTransactionStatFields[key] !== 0)) 
      {
        fixes[key] = cashboxTransactionStatFields[key] || 0
      }
    }
  }

  if (log) {
    console.log('new stat fields', unifiedSaleStatFields)
    console.log('old stat fields', existingDailyStat)
  }
      
  const hasFixes = Object.keys(fixes).length > 0
  // A repair is SUPPRESSED, not skipped, when an automatic pass runs on a device whose timezone is
  // not the store's: that device resolves a different business day, so writing absolute totals from
  // it would move the drift rather than remove it. Manual repairs (`automaticFixes === false`) run
  // regardless, because the operator chose the day.
  // `Boolean(...)`, not the bare `&&`: `userInDifferentTimeZoneAtom` is not guaranteed to hold a
  // boolean, and the short circuit would otherwise put its falsy value (e.g. `undefined`) into
  // `meta.suppressedByTimezone`, which the declaration and the Sentry tag both promise is a boolean.
  const suppressedByTimezone = Boolean(hasFixes && update && userInDifferentTimeZone && automaticFixes)
  let applied = false

  if (hasFixes && update && !suppressedByTimezone) {
    // Log the dailyStats update
    dbWriteLogger.logWrite(
      'dailyStats',
      existingDailyStat.id,
      fixes,
      'set',
      {merge: true}
    )

    reportFirestoreOnlyWriterUsage('validateDailyStats', db?.id)
    await db.collection('dailyStats').doc(existingDailyStat.id).set(fixes, {merge: true})
    applied = true
    if (log) console.log('updated')
  }

  // ATL-1101 — a discrepancy in the books is REPORTED, not merely printed to a console nobody reads.
  // `dailyStats` is what the Sales report renders, so a drift here is wrong money on a merchant's
  // screen; ATL-779 asks for its fleet incidence and until now the app told no one. Fingerprinted on
  // (org, store) — see the note at the fingerprint itself for why NOT on the key set.
  if (hasFixes) {
    const previousValues = {}
    for (const key of Object.keys(fixes)) {
      previousValues[key] = existingDailyStat[key] === undefined ? null : existingDailyStat[key]
    }
    Sentry.captureMessage(
      suppressedByTimezone
        ? 'dailyStats drift found but the repair was suppressed (device timezone is not the store\'s)'
        : 'dailyStats disagreed with the sale documents that back it',
      {
        level: suppressedByTimezone ? 'warning' : 'info',
        tags: {
          daily_stats_drift: true,
          daily_stats_repair_applied: applied,
          daily_stats_repair_suppressed: suppressedByTimezone,
        },
        // Grouped on (org, store) — NOT on the key set. Most of `fixes` is dynamic per-cashier and
        // per-wallet keys, so a store that drifts on Tuesday and again on Thursday almost never
        // produces the same key list, and folding the list into the fingerprint would mint a fresh
        // Sentry issue per day — exactly the outcome this is meant to avoid. The keys are still in
        // the context below, where they are readable without splitting the issue.
        fingerprint: ['daily-stats-drift', organizationId || 'unknown', store],
        contexts: {
          daily_stats_drift: {
            statId: existingDailyStat.id,
            store,
            organizationId,
            automaticFixes,
            update,
            applied,
            suppressedByTimezone,
            // The corrected values, and the ones they replace — both sides, so the report says what
            // was wrong and not merely that something was. Built with a plain loop rather than
            // `Object.fromEntries`: no shipped app source uses that static today, and a missing
            // Hermes builtin fails at runtime on native only (the ATL-980 class), where lint and
            // the web bundle would both stay green.
            fixes,
            previous: previousValues,
          },
        },
      }
    )
  }

  if (log) console.log('done', localDate, fixes)
  
  // End database write logging
  dbWriteLogger.endFunction(logId)
  
  return [!hasFixes, fixes, unifiedSaleStatFields, { applied, skipped: null, suppressedByTimezone }]
}

/**
 * Validates and optionally fixes daily statistics document ID consistency
 * 
 * This function checks if the daily statistics document ID matches the expected
 * format based on the document's date. If there's a mismatch and fix is enabled,
 * it updates the document with the correct date.
 * 
 * @param {Object} dailyStat - Daily statistics document with id, date, and store properties
 * @param {boolean} fix - Whether to fix the date if there's a mismatch
 * @returns {Promise<void>} Logs validation results and optionally updates the document
 */
export const validateDailyStatsId = async (dailyStat, fix=false) => {
  // Handle both Firestore timestamps (with toDate()) and Supabase dates (Date objects or strings)
  let date = normalizeDate(dailyStat?.date)
  // Skip validation if date is invalid
  if (!date || isNaN(date.getTime())) {
    console.warn('validateDailyStatsId: date is missing or invalid, skipping validation', dailyStat)
    return
  }
  let store = dailyStat?.store || dailyStat?.store_id || dailyStat?.storeId
  if (!store) {
    console.warn('validateDailyStatsId: store is missing, skipping validation', dailyStat)
    return
  }
  let correctDailyStatId = getDailyStatID(store, date)
  if (!correctDailyStatId) {
    console.warn('validateDailyStatsId: could not generate correct ID, skipping validation', dailyStat)
    return
  }
  if (correctDailyStatId !== dailyStat.id) {
    console.log('dailyStat id and date mismatch', date, dailyStat.id, dailyStat)
    if (fix) {
      if (!dailyStat.id || typeof dailyStat.id !== 'string') {
        console.warn('validateDailyStatsId: dailyStat.id is missing or invalid, cannot fix', dailyStat)
        return
      }
      const [day, month, year] = dailyStat.id.split('-')
      if (!day || !month || !year) {
        console.warn('validateDailyStatsId: dailyStat.id format is invalid, cannot fix', dailyStat.id)
        return
      }
      // Create a new date object from the ID components
      const correctDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 8, 0, 0, 0)
      // Validate correctDate before using it
      if (isNaN(correctDate.getTime())) {
        console.warn('validateDailyStatsId: could not create valid date from ID components', dailyStat.id)
        return
      }
      const localDate = getLocalStoreDate(store, correctDate)
      // Validate localDate before using it
      if (!localDate || isNaN(localDate.getTime())) {
        console.warn('validateDailyStatsId: localDate is invalid, cannot fix', dailyStat.id)
        return
      }
      // Update the document with the correct date
      if (dailyStat.ref && typeof dailyStat.ref.update === 'function') {
        reportFirestoreOnlyWriterUsage('validateDailyStatsId')
        await dailyStat.ref.update({date: localDate})
        console.log('updated date of dailyStat', dailyStat.id, localDate)
      } else {
        console.warn('validateDailyStatsId: dailyStat.ref is missing or invalid, cannot update', dailyStat)
      }
    }
  }
}

/**
 * How long `saveProductStockSnapshot` will wait for the product catalogue and the store list to
 * arrive before giving up on the day. The old code slept a flat 10s and then took whatever the
 * atoms happened to hold, which on a slow first load meant snapshotting an EMPTY catalogue.
 */
export const STOCK_SNAPSHOT_READY_TIMEOUT_MS = 60000
export const STOCK_SNAPSHOT_POLL_INTERVAL_MS = 1000

/**
 * The cell written for a product that has no stock entry for this store. Deliberately the STRING
 * 'NaN' and not a number: it means "this snapshot does not know this product's stock here", which
 * is not the same claim as "there were zero". Every snapshot ever written uses this marker, so the
 * representation is frozen — the readers (`screens/Stats/productStockAmounts.js`) are what must
 * understand it. See ATL-1123.
 */
export const STOCK_SNAPSHOT_UNKNOWN_CELL = 'NaN'

/**
 * Snapshot ids this app session has already written (or found already present).
 *
 * `subscribeToDailyStats` — the caller — runs several times during startup and again on every day
 * rollover, and its invocations overlap. Without a claim taken BEFORE the first await, two runs
 * would each read "no snapshot for today yet" and then both write, the second one overwriting the
 * morning's opening stock with whatever the shelves hold by then.
 */
const claimedStockSnapshotIds = new Set()

/**
 * Whether this session has already told Sentry the catalogue never loaded. `subscribeToDailyStats`
 * runs 2-4 times per app start and again on every rollover, and an organization with an empty
 * product catalogue (a brand-new one, or a device whose products subscription never lands) hits the
 * refusal on EVERY one of them. Without this the warning is emitted per invocation, per launch,
 * fleet-wide — a quota drain that says nothing the first event did not.
 */
let stockSnapshotCatalogueMissReported = false

/**
 * Whether this session has already told Sentry that `dbAtom` was empty when the writer ran. The
 * `no-db` refusal loses the whole day for the whole org, and `subscribeToDailyStats` may not run
 * again before the next app start (a single-store org calls it once), so it must not be the one
 * refusal path that says nothing — silence about a missing snapshot is the exact failure mode of
 * ATL-1123.
 */
let stockSnapshotNoDbReported = false

/**
 * Whether this session has already told Sentry that the STORE list never loaded. Same reason as
 * `stockSnapshotNoDbReported`: for a single-store organization `subscribeToDailyStats` runs once
 * per app session (the stores listener only re-invokes it when there is more than one store), so
 * a `no-stores` refusal loses the day silently unless it is reported.
 */
let stockSnapshotNoStoresReported = false

/** Test seam: forget this session's claims. */
export const __resetStockSnapshotClaims = () => {
  claimedStockSnapshotIds.clear()
  stockSnapshotCatalogueMissReported = false
  stockSnapshotNoDbReported = false
  stockSnapshotNoStoresReported = false
}

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

// `date` is written ONLY when the daily-stat document does not exist yet, and it is the RAW
// instant — the same frame every other `dailyStats` writer uses (`firebase.ts` writes
// `paymentInfo.paymentDate || new Date()`, `inventory.ts` writes `info.supplyDate`).
//
// It is the field `getStatsForDays` range-queries (`where('date','>=',…)`), and those bounds
// are TRUE INSTANTS built in the store's zone by `dateRangeWithTimezoneAndAutoclose`. So the
// value stored here has to be a true instant too. `getLocalStoreDate` is not: it returns the
// store's WALL CLOCK re-expressed as a device-local instant, i.e. the real instant shifted by
// (device offset − store offset). For an owner opening the app from another country that is
// hours, and hours across a range bound puts the document in the WRONG range — which for the
// P&L is not a cosmetic mislabel: `computeStockAmountsForRange` takes `docs[0]` /
// `docs[docs.length - 1]` as the opening and closing boundaries, so a document pulled into a
// range it does not belong to makes the report read a different day's stock snapshot. That is
// the ATL-1111/ATL-1112 defect written from the client side.
//
// Merging it into a document somebody else already created is the other half of the same
// hazard, so `date` is only ever stamped on creation (without it the range query cannot find
// the document at all).
//
// `createIfMissing` is the other guard, and it is what keeps YESTERDAY's write from
// inventing a trading day. `set(..., {merge: true})` CREATES an absent document, so on the
// first app start after a day the shop was closed (nobody opened the app, so no sale, no
// shift, no daily stat) this used to conjure `dailyStats/<yesterday>` carrying nothing but
// `endProductAmount`/`store`/`date`. `getStatsForDays` range-queries `date`, so that ghost
// then reads as a day the business traded: it adds an all-zero row to every P&L chart, and
// when it lands on the first or last day of a reported range it becomes a BOUNDARY whose
// `-stock` snapshot was never written — so `computeStockAmountsForRange` reports the whole
// range's opening and closing stock as unavailable, permanently. Today's document is created
// (that day IS being snapshotted, and the snapshot is what the P&L reads); yesterday's
// closing amount is a merge into a day that already exists or nothing at all — it has no
// reader of its own, so there is nothing to lose by not creating it.
const withDate = async (ref, payload, date, { createIfMissing }) => {
  const existingStat = await ref.get()
  const exists = snapshotExists(existingStat)
  if (!exists && !createIfMissing) return false
  // `filterUndefined`: `organizationId` is read off the store record and Firestore rejects an
  // undefined value outright — a throw here would abandon the day for this store.
  await ref.set(
    filterUndefined(exists ? payload : { ...payload, date }),
    { merge: true },
  )
  return true
}

/**
 * Wait until both the store list and the product catalogue have loaded, or the timeout expires.
 * Returns the two dictionaries (possibly still empty — the caller decides what to do about that).
 */
const waitForCatalogue = async (jotaiStore, timeoutMs, pollIntervalMs) => {
  const deadline = Date.now() + timeoutMs
  for (;;) {
    const stores = jotaiStore.get(storesDictAtom)
    const products = jotaiStore.get(productsDictAtom)
    const ready = stores && Object.keys(stores).length > 0 && products && Object.keys(products).length > 0
    if (ready || Date.now() >= deadline) return { stores, products }
    await delay(Math.max(1, Math.min(pollIntervalMs, deadline - Date.now())))
  }
}

/**
 * Saves today's product stock snapshot for every store.
 *
 * ATL-1123 — this is the writer behind the P&L's "product cost at the beginning / at the end"
 * lines. `screens/Stats/ProfitStats.js` reads `productStockSnapshots/<d-M-y-storeId>-stock` for the
 * first and last day of the reported range; with no document those lines resolve to 0 and the stock
 * delta silently reads as "no change".
 *
 * Its only call site (`AppStack.subscribeToDailyStats`) was deleted in 896466fd as collateral of a
 * daily-stats refactor on 2026-01-12; the first release carrying that deletion was v6.9.0 on
 * 2026-06-05, which is why production stopped writing snapshots on 2026-06-04.
 *
 * Two properties this function must keep, both learned from that outage:
 *
 * 1. **It writes to Firestore in BOTH read modes.** Stock snapshots have no Supabase home — no
 *    table, no dual-write, no migration — so `getProductStockSnapshot` reads Firestore regardless
 *    of `shouldUseSupabase()`, and so must this. Crucially the "already snapshotted today?" guard
 *    may NOT be the `dailyStats.productStockSnapshot` flag it used to be: the Supabase `daily_stats`
 *    VIEW has no such column, so in Supabase read mode that flag is permanently undefined and the
 *    guard would never fire — re-snapshotting on every app start and overwriting the morning's
 *    opening stock with the evening's shelves. The guard reads the snapshot document itself.
 * 2. **It refuses rather than writes a snapshot it cannot stand behind.** An empty or partly-loaded
 *    catalogue produces a document that reads as a genuine zero opening stock forever after, and
 *    the `productStockSnapshot: true` flag would stop the day from ever being retried.
 *
 * @param {Object} [options]
 * @param {Date}   [options.now] - clock override for tests.
 * @param {number} [options.readyTimeoutMs]
 * @param {number} [options.pollIntervalMs]
 * @returns {Promise<{written: Array, skipped: Array, refused: Array, failed: Array}>} a report of
 *   what happened per store, so callers and tests can assert on it instead of on console output.
 */
export const saveProductStockSnapshot = async ({
  now = null,
  readyTimeoutMs = STOCK_SNAPSHOT_READY_TIMEOUT_MS,
  pollIntervalMs = STOCK_SNAPSHOT_POLL_INTERVAL_MS,
} = {}) => {
  const report = { written: [], skipped: [], refused: [], failed: [] }
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom)
  if (!db) {
    report.refused.push({ reason: 'no-db' })
    if (!stockSnapshotNoDbReported) {
      stockSnapshotNoDbReported = true
      Sentry.captureMessage('productStockSnapshot skipped: no organization db handle', {
        level: 'warning',
        tags: { feature: 'productStockSnapshot' },
      })
    }
    return report
  }

  const { stores, products } = await waitForCatalogue(jotaiStore, readyTimeoutMs, pollIntervalMs)

  if (!stores || Object.keys(stores).length === 0) {
    // Usually a wait rather than a loss — a later `subscribeToDailyStats` (stores subscription,
    // day rollover) runs this again. But NOT always, and the exception is the common shape: the
    // stores listener only re-invokes `subscribeToDailyStats` for a MULTI-store org
    // (`AppStack.js`: `if (Object.values(docs).length > 1)`), so a single-store organization gets
    // exactly one attempt per app session. If `storesDictAtom` is still empty when the timeout
    // expires — a launch with no network and a cold Firestore cache — that day's opening stock is
    // lost for the whole session with nothing recorded anywhere. Report it once, like the two
    // refusals below: an unexplained missing snapshot is the exact failure mode of ATL-1123.
    report.refused.push({ reason: 'no-stores' })
    if (!stockSnapshotNoStoresReported) {
      stockSnapshotNoStoresReported = true
      Sentry.captureMessage('productStockSnapshot skipped: store list never loaded', {
        level: 'warning',
        tags: { feature: 'productStockSnapshot' },
        extra: { readyTimeoutMs },
      })
    }
    return report
  }
  if (!products || Object.keys(products).length === 0) {
    // Refuse loudly: writing here would freeze an empty opening stock into the books for the day.
    report.refused.push({ reason: 'no-products' })
    if (!stockSnapshotCatalogueMissReported) {
      stockSnapshotCatalogueMissReported = true
      Sentry.captureMessage('productStockSnapshot skipped: product catalogue never loaded', {
        level: 'warning',
        tags: { feature: 'productStockSnapshot' },
        extra: { storeCount: Object.keys(stores).length, readyTimeoutMs },
      })
    }
    return report
  }

  const today = now ? new Date(now) : new Date()
  const yesterday = addDays(today, -1)

  for (const store of Object.keys(stores)) {
    // `snapshotId`/`statId` are declared out here so the catch can release the claim, but every
    // call that can THROW is inside the try. `getDailyStatID` reads jotai and formats a date, and
    // a throw from it out here would abandon every remaining store for the day — the same
    // all-or-nothing failure this rewrite removed from the per-product loop below.
    let snapshotId = null
    let statId = null
    try {
      statId = getDailyStatID(store, today)
      const yesterdayStatId = getDailyStatID(store, yesterday)
      if (!statId || !yesterdayStatId) {
        // Without a business-date id there is no document to write to; `statId + '-stock'` would
        // otherwise address the literal document "null-stock".
        report.refused.push({ store, reason: 'no-stat-id' })
        continue
      }
      snapshotId = statId + '-stock'
      // Claim before the first await, so overlapping runs cannot both proceed.
      if (claimedStockSnapshotIds.has(snapshotId)) {
        report.skipped.push({ store, snapshotId, reason: 'claimed-this-session' })
        continue
      }
      claimedStockSnapshotIds.add(snapshotId)

      const existing = await db.collection('productStockSnapshots').doc(snapshotId).get()
      if (snapshotExists(existing)) {
        report.skipped.push({ store, snapshotId, reason: 'already-exists' })
        continue
      }

      const productsByStore = { date: today }
      let inStockPurchaseAmount = 0
      let unknownActiveProducts = 0
      for (const productId of Object.keys(products)) {
        const product = products[productId]
        const raw = product?.storeStock?.[store]
        // `product.storeStock` can be absent entirely on a malformed product doc; reading through
        // it unguarded used to throw and abandon EVERY remaining store, not just this product.
        // ONE parsed value feeds both the cell and the valuation below. They used to be derived
        // separately — `typeof raw === 'number'` for the cell, `Number(raw) || 0` for the money —
        // so the same product could be recorded as "we have no figure" in the snapshot and counted
        // as a real quantity in `startProductAmount`, and an absent figure was asserted as a stock
        // of zero in a document that is customer books.
        const stock = typeof raw === 'number' && Number.isFinite(raw) ? raw : null
        productsByStore[productId] = stock === null ? STOCK_SNAPSHOT_UNKNOWN_CELL : stock
        // `for...in` over a dictionary yields KEYS. The old line read `product.activeInStore` off
        // the key STRING, which is always undefined, so this total was always exactly 0 — the
        // amount was written into the books, and was a lie, for as long as the writer ran.
        if (product?.activeInStore?.[store] && !product?.isComposite) {
          if (stock === null) unknownActiveProducts += 1
          else inStockPurchaseAmount += stock * (Number(product?.purchasePrice) || 0)
        }
      }

      // The daily-stat amounts are written BEFORE the snapshot document, because the snapshot
      // document is the guard: `snapshotExists(existing)` above skips a store whose snapshot is
      // already there. Writing the snapshot first meant that a failure in either daily-stat write
      // left a day permanently half-written — the retry would find the snapshot present, skip, and
      // never write `startProductAmount`/`endProductAmount` at all. In this order a failure at any
      // point leaves the guard unset, so the next run redoes the whole store.
      reportFirestoreOnlyWriterUsage('saveProductStockSnapshot', db?.id)
      await withDate(
        db.collection('dailyStats').doc(statId),
        {
          productStockSnapshot: true,
          startProductAmount: inStockPurchaseAmount,
          store,
          organizationId: stores[store]?.organizationId,
        },
        today,
        { createIfMissing: true },
      )
      const closingStatWritten = await withDate(
        db.collection('dailyStats').doc(yesterdayStatId),
        {
          endProductAmount: inStockPurchaseAmount,
          store,
          organizationId: stores[store]?.organizationId,
        },
        yesterday,
        { createIfMissing: false },
      )
      await db.collection('productStockSnapshots').doc(snapshotId).set(productsByStore)
      // `unknownActiveProducts` is how much of the store's active catalogue the amount above
      // could NOT value. It is reported rather than folded into the number: the P&L reads the
      // snapshot, not this field, and a caller that needs to know the total is understated has to
      // be able to ask.
      report.written.push({ store, snapshotId, statId, inStockPurchaseAmount, unknownActiveProducts, closingStatWritten })
    } catch (error) {
      // Release the claim so the next `subscribeToDailyStats` of this session can retry, and make
      // the failure visible: a missing snapshot is otherwise indistinguishable from a real zero.
      if (snapshotId) claimedStockSnapshotIds.delete(snapshotId)
      report.failed.push({ store, snapshotId, error: error?.message })
      Sentry.captureException(error, {
        tags: { feature: 'productStockSnapshot', books_write_error: 'product-stock-snapshot-write' },
        extra: { store, snapshotId, statId },
      })
    }
  }

  return report
}

/**
 * Compares product report sales totals with daily statistics to ensure consistency
 * 
 * This function validates that the sum of sales amounts from product reports
 * matches the sales amount recorded in daily statistics. It can optionally
 * trigger validation and fixes if discrepancies are found.
 * 
 * @param {Object} range - Date range object with startDate and endDate
 * @param {string} store - Store identifier to filter data
 * @param {boolean} update - Whether to update statistics if discrepancies are found
 * @returns {Promise<void>} Logs comparison results and optionally applies fixes
 */
export const compareProductReportAndDailyStats = async (range, store, update) => {
  // ATL-1329: in Supabase-read mode the two sides have no common day. The product report docs come
  // from `/reports/product-report`, which buckets a row by its UTC date-part; `dailyStats` is the
  // store's business day. For an Almaty store the report's "day" runs 05:00–05:00 local, so every
  // night sale — and every day of a UTC− store — would print a false `mismatch`, while the repair
  // branch is refused by `validateDailyStats` anyway. A comparison that cannot agree when the books
  // are right proves nothing; decline it and say why.
  if (shouldUseSupabase()) {
    console.log('compareProductReportAndDailyStats skipped: supabase-read-mode (product report days are UTC, dailyStats days are business days)')
    return
  }
  let jotaiStore =  getDefaultStore()
  let db = jotaiStore.get(dbAtom)
  let productsDict = jotaiStore.get(productsDictAtom)
  let localRange = dateRangeWithTimezoneAndAutoclose(range, [store])
  let productReports = await getProductStatsForDays(db, localRange, store, true)
  let dailyStats = await getStatsForDays(db, localRange, store, true)
  let date = addDays(localRange.startDate, -1)
  do {
    date = addDays(date, 1)
    let productSaleSum = 0
    for (let product in productReports[format(getLocalStoreDate(store, date), 'd/M/y E')]) {
      if (product !== 'store' && product !== 'date') {
        productSaleSum += productReports[format(getLocalStoreDate(store, date), 'd/M/y E')][product]?.saleAmount || 0
      }
    }
    if (productSaleSum !== (dailyStats[format(getLocalStoreDate(store, date), 'd/M/y E')]?.saleAmount || 0)) {
      console.log(date, 'mismatch', productSaleSum, dailyStats[format(getLocalStoreDate(store, date), 'd/M/y E')]?.saleAmount)
      if (update) {
        // This is an internal recursive call - pass null for screenName, logger will extract from call stack
        let [hadFixes, fixes, ds, meta] = await validateDailyStats(date, store, true, {sales: true}, null, true, false, null)
        // ATL-1101 — a REFUSAL is not a comparison, and must not fall through to the repair below.
        //
        // Every early exit now returns a uniform 4-tuple instead of a bare `undefined` (that
        // `undefined` was a latent TypeError right here, since this destructures the result
        // directly). But a uniform shape makes a bail-out READ like a clean comparison: `ds` is
        // `{}`, `ds.saleAmount` is `undefined`, `productSaleSum !== undefined` is always true, and
        // the branch below calls `generateProductReportForDate`, which `set()`s
        // `dailyProductStats/<id>`. In Supabase-read mode — where `validateDailyStats` refuses for
        // exactly the reason that the sale getters are row-capped — that would rebuild a Firestore
        // product report out of a truncated read: the very write the refusal exists to prevent,
        // reached by the caller that did not check why it was refused.
        if (meta?.skipped) {
          console.log(date, 'skipped', meta.skipped)
          continue
        }
        if (productSaleSum !== ds.saleAmount) {
          let pR = await generateProductReportForDate(date, store)
          let newSum = 0
          for (let product in pR) {
            if (product !== 'store' && product !== 'date') {
              newSum += pR[product]?.saleAmount || 0
            }
          }
          if (newSum !== ds.saleAmount) {
            console.log(date, 'not fixed')
          } else {
            console.log(date, 'fixed, pR error')
          }
        } else {
          console.log(date, 'fixed, dS error')
        }        
      }
    } else {
      console.log(date, 'ok')
    }
  } while (format(date, 'd/M/y E') !== format(localRange.endDate, 'd/M/y E'))
}